import { useEffect, useRef, useContext } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { LayoutContext } from '@/app/ClientRootHomepage';
import {getScroller, setupLenyStyle} from "@/app/homepage/utils";
import {SIZES} from "@/components/constants";
import {useMediaQuery} from "react-responsive";

gsap.registerPlugin(ScrollTrigger);

const BusinessField = () => {
    const wrapRef = useRef(null);
    const { rootRef } = useContext(LayoutContext);
    const isTablet = useMediaQuery({
        query: `(max-width: ${SIZES.TABLET})`
    });


    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const scroller = getScroller(rootRef);
        const contentEl = (scroller && scroller.querySelector && scroller.querySelector(".root")) || (scroller && scroller.firstElementChild) || scroller;
        const { cleanup } = setupLenyStyle({ wrap, scroller, contentEl });
        const contentSections = Array.from(wrap.querySelectorAll(".fade-section"));
        const triggers = [];
        const tweenItems = [];
        contentSections.forEach((item) => {
            gsap.set(item, { opacity: 0, y: 30, willChange: "transform, opacity" });
            const tweenGsap = gsap.to(item, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: "power2.out",
                paused: true,
                force3D: false,
            });
            const scrollGsap = ScrollTrigger.create({
                trigger: item,
                start: "top 70%",
                end: "top 45%",
                scroller,
                toggleActions: "play none none reverse",
                onEnter: () => tweenGsap.play(),
                onEnterBack: () => tweenGsap.play(),
                onLeaveBack: () => tweenGsap.reverse(),
            });
            tweenItems.push(tweenGsap);
            triggers.push(scrollGsap);
        });



        const onResize = () => ScrollTrigger.refresh();

        window.addEventListener("resize", onResize);

        return () => {
            triggers.forEach((st) => st.kill());
            tweenItems.forEach((tw) => tw.kill());
            window.removeEventListener("resize", onResize);
            cleanup();
        };
    }, [rootRef, isTablet]);



    return(
        <BusinessFieldWrap ref={wrapRef}>
            <h2 className={"title-area fade-section"}>
                {
                    !isTablet ?
                        <>면역을 중심으로<br/>진단과 바이오 연구를 연결하는 기업, 웅비메디텍</>
                        :
                        <>면역을 중심으로<br/>진단과 바이오 연구를 연결하는 기업,<br/>웅비메디텍</>
                }
            </h2>
            <div className={"visual-wrap fade-section"}>
                <div className={"visual-item"}>
                    <div className={"visual-item-inner"}>
                        <div className={"half-bg"}>Precision medicine. Personalized Thx.</div>
                        <div className={"half-item"}>
                            <div>Drug development</div>
                            <div>Cell & Gene Thx</div>
                        </div>
                        <div className={"half-item"}>
                            <div>CDMO. Biosimilar</div>
                            <div>Stem cell. Organoid</div>
                        </div>
                        <div className={"half-item-asymmetry"}>
                            <div>GMP. Protein. Small molec.</div>
                            <div className={"little"}>NGS</div>
                        </div>
                        <div className={"half-item-asymmetry"}>
                            <div>Antibody. Detection kit</div>
                            <div className={"little"}>Autoimmune</div>
                        </div>
                        <div className={"half-item-asymmetry mt"}>
                            <div>Bio-Research</div>
                            <div className={"little"}>Diagnostics</div>
                        </div>
                        <div className={"half-bg"}>Immunology. Molecular Biology</div>
                    </div>
                    <p className={"visual-item-desc"}>
                        {
                            !isTablet ?
                            <>*Orange색은 진단 분야를 가리키며, Green색은 바이오연구 분야를 의미합니다</>
                            :
                            <>*Orange색은 진단 분야를 가리키며,<br /> Green색은 바이오연구 분야를 의미합니다</>
                        }

                    </p>
                </div>
            </div>
            <div className={"section-area"}>
                <div className={"section-wrap fade-section"}>
                    <div className={"section-title"}>
                        {
                            !isTablet ?
                                <>
                                    면역, 그 하나의 뿌리에서<br/>
                                    진단과 바이오 연구를<br/>
                                    연결하다
                                </>
                                :
                                <>
                                    면역, 그 하나의 뿌리에서<br/>
                                    진단과 바이오 연구를 연결하다
                                </>
                        }
                    </div>
                    <div className={"section-cont"}>
                        <div className={"text-box"}>
                            <p>웅비메디텍은 진단(Diagnostics)과 바이오 연구(Bio Research) 분야에서 면역을 중심으로 한 통합된 솔루션을 제공합니다. 서로 다른
                                시장처럼 보일 수 있지만, 두 영역 모두 “면역”이라는 공통된 과학적 기반 위에서 유기적으로 연결되어 있습니다.</p>
                        </div>
                        <div className={"cont01-box"}>
                            <div className={"box-title"}>
                                <span>진단 제품<br/></span>
                                : 정확한 분석, 정밀한 판단
                            </div>
                            <div className={"box-inner"}>
                                <div className={"box-inner-top"}>
                                    <div className={"inner-top-list"}>
                                        <div className={"inner-list-item"}>
                                            <div className={"item-chip"}>
                                                분자진단<br/>
                                                (Molecular Diagnostics)
                                            </div>
                                            <span>-</span>
                                            <div className={"item-text"}>NGS 기반 유전체 질환 진단</div>
                                        </div>
                                        <div className={"inner-list-item"}>
                                            <div className={"item-chip"}>
                                                자가면역질환 진단<br/>
                                                (Autoimmune Diagnostics)
                                            </div>
                                            <span>-</span>
                                            <div className={"item-text"}>고신뢰성 진단검사용 진단 키트</div>
                                        </div>
                                    </div>
                                </div>
                                <div className={"box-inner-bot"}>
                                    <p>“웅비메디텍은 창업 초기부터 대학병원 중심의 진단 시장에서 면역분야에 집중해 왔으며, 그 경험은 이후의 모든 사업 확장의 기초가
                                        되었습니다.”</p>
                                </div>
                            </div>
                        </div>
                        <div className={"cont01-box"}>
                            <div className={"box-title"}>
                                <span className={"gr"}>바이오 연구 제품<br/></span>
                                : 면역과 세포 연구를 위한 분석 Tools
                            </div>
                            <div className={"box-inner"}>
                                <div className={"box-inner-top"}>
                                    <div className={"inner-top-list gr"}>
                                        <div className={"inner-list-item"}>
                                            <div className={"item-chip"}>
                                                R&D Systems 제품군 기반
                                            </div>
                                        </div>
                                        <div className={"inner-list-item"}>
                                            <div className={"item-chip"}>
                                                {
                                                    !isTablet ?
                                                        <>
                                                            면역학 (Immunology), 세포생물학 (Cell Biology) 연구를 위한 고품질 분석 시약 및 솔루션
                                                        </>
                                                        :
                                                        <>
                                                            면역학 (Immunology),<br/> 세포생물학 (Cell Biology) 연구를 위한 고품질 분석 시약 및 솔루션
                                                        </>
                                                }

                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={"box-inner-bot"}>
                                    <p>“진단 분야에서 구축한 정확성과 일관성을 바이오 연구에도 적용하여, 고객에게 신뢰할 수 있는 연구 환경을 제공합니다.”</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"section-wrap fade-section"}>
                    <div className={"section-title"}>
                        {
                            !isTablet ?
                                <>
                                    왜 진단과<br/>
                                    바이오연구 분야가<br/>
                                    연결되어 있을까요?
                                </>
                                :
                                <>
                                    왜 진단과 바이오연구 분야가<br/>
                                    연결되어 있을까요?
                                </>
                        }
                    </div>
                    <div className={"section-cont"}>
                        <div className={"text-box"}>
                            <p>진단 제품과 연구 제품은 각기 다른 시장과 고객을 대상으로 하지만, 면역 메커니즘을 공통 기반으로 삼고 있습니다.
                                당사의 제품군은 Proteomics(단백체학)와 Genomics(유전체학) 양축으로 하여 구성되며, 이는 오늘날의 정밀의료(Precision
                                Medicine), 개인맞춤의료(Personalized Medicine)로 연결되는 동일한 길을 향하여 함께 걷고 있습니다.
                            </p>
                        </div>
                        <div className={"cont01-box box2"}>
                            <div className={"box2-title"}>고객과 시장 확장</div>
                            <div className={"box-inner"}>
                                <div className={"box-inner-top"}>
                                    <div className={"inner-top-list"}>
                                        <div className={"inner-list-item"}>
                                            <div className={"item-chip"}>
                                                진단제품
                                            </div>
                                            <span>-</span>
                                            <div className={"item-text"}>대학병원, 임상의 대상(Academia 중심)</div>
                                        </div>
                                        <div className={"inner-list-item"}>
                                            <div className={"item-chip"}>
                                                바이오 연구 제품
                                            </div>
                                            <span>-</span>
                                            <div className={"item-text"}>연구소, 바이오벤처, Biopharma 기업 등 산학연계로 확대</div>
                                        </div>
                                        <div className={"inner-list-item"}>
                                            <div className={"item-chip"}>
                                                Biopharma 고객과의 협업
                                            </div>
                                            <span>-</span>
                                            <div className={"item-text"}>Biopharma 고객과의 협업 신약개발에 기여하며 K-Bio의 성장에 함께 하고 있습니다.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {
                            !isTablet &&
                            <div className={"text-box"}>
                                <p>웅비는 과거 수많은 신기술 브랜드를 성장시킨 경험과 기술력을 축적하여 다져진 전문성을 기반으로 진단과 바이오연구 고객과 함께 성장하는 Portfolio로
                                    Upgrade해왔습니다.
                                </p>
                            </div>
                        }
                    </div>
                </div>
                <div className={"section-wrap fade-section"}>
                    <div className={"section-title"}>
                        일관성과 연결성 Consistency<br/>
                        & Connectivity
                    </div>
                    <div className={"section-cont"}>
                        <div className={"text-box"}>
                            <p>진단 시장과 바이오 연구 시장은 서로 다른 시장입니다. 그러나, 웅비는 진단 제품으로 대학병원 중심의 Academia market을 확보한 후, 대학 내
                                바이오벤쳐 등 산학연계를 통해 Bio-industry시장을 접목하여 Biopharma시장으로 확장했습니다.</p>
                            <p>즉, 진단 시장에서 쌓아온 신뢰성과 정확성을 생명과학 연구에 그대로 이식했습니다. 더 나아가 Biopharma고객들이 신약을 개발하여 K-Bio 가 더욱
                                성장하는데 기여할 수 있게 되었습니다. 궁극적으로 웅비의 모든 제품은 Precision medicine,
                                Personalized Medicine의 한 방향으로 연결되어 있습니다.</p>
                            <p>이는 웅비메디텍만의 사업적 시너지이자 고객께 드릴 수 있는 가장 큰 강점입니다.</p>
                        </div>
                    </div>
                </div>
                <div className={"section-wrap fade-section"}>
                    <div className={"section-title"}>
                        미래를 준비하는<br/>
                        웅비메디텍
                    </div>
                    <div className={"section-cont"}>
                        <div className={"text-box"}><p>정밀의료의 미래는 Multiomics에 있습니다.
                            웅비메디텍은 이미 아래 3가지 핵심 분석 기술에서 경험과 시스템을 갖추고 있으며, 이를 바탕으로 다가오는 Multiomics 분석 시대에 최적화된 포트폴리오를 확장해
                            나가고 있습니다.</p></div>
                        <div className={"feature-box"}>
                            <div className={"box-inner"}>
                                <p>Proteomics</p>
                                <span>단백체 분석</span>
                            </div>
                            <div className={"box-inner"}>
                                <p>Genomics</p>
                                <span>유전체 분석</span>
                            </div>
                            <div className={"box-inner"}>
                                <p>Transcriptomics / NGS</p>
                                <span>전사체 분석</span>
                            </div>
                        </div>
                        <div className={"feature-info-text"}>“미래의 연구에 필요한 모든 것,
                            면역 기반 진단과 바이오연구 제품 공급 전문 기업, 웅비메디텍이 함께합니다.”</div>
                    </div>
                </div>
            </div>
        </BusinessFieldWrap>
    )
}
const BusinessFieldWrap = styled.div`
    padding: 100px 114px 160px;
    .title-area {
        font-weight: 700;
        font-size: 48px;
        line-height: 58px;
        text-align: center;
        letter-spacing: -0.02em;
        color: #4939B1;
        .char{
            display: inline-block;
        }
        .char.is-space {
           width: 0.3em;
        }
    }
    .visual-wrap {
        margin: 160px 0 160px;
        width: 100%;
        height: 680px;
        border-radius: 20px;
        overflow: hidden;
        background: linear-gradient(180deg, #2E0B7E 0%, #0A0323 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        .visual-item{
            width: 848px;
            .visual-item-desc{
                margin-top: 8px;
                text-align: right;
                font-weight: 400;
                font-size: 16px;
                line-height: 24px;
                color: #D1D1D1;
            }
            .visual-item-inner{
                width: 100%;
                background: #FFFFFF;
                border-radius: 20px;
                overflow: hidden;
                padding: 18px;
                & > div {
                    font-weight: 700;
                    font-size: 24px;
                    color: #FFFFFF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 62px;
                }
                .half-bg{
                    background: linear-gradient(180deg, #31A95B 0 50%, #ED8823 50% 100%);
                    overflow: hidden;
                    border-radius: 4px;
                }
                .half-item{
                    gap: 8px;
                    margin-top: 10px;
                    & > div{
                        height: 100%;
                        width: calc(50% - 4px);
                        flex-shrink: 0;
                        background: #31A95B;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        overflow: hidden;
                        border-radius: 4px;
                    }
                }
                .half-item-asymmetry{
                    gap: 8px;
                    margin-top: 10px;
                    &.mt{
                        margin-bottom: 10px;
                    }
                    & > div{
                        height: 100%;
                        background: #31A95B;
                        width: 100%;
                        overflow: hidden;
                        border-radius: 4px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        &.little{
                            width: 306px;
                            flex-shrink: 0;
                            background: #ED8823;
                        }

                    }
                }
            }
          
        }
    }
    .section-area{
        .section-wrap{
            display: flex;
            align-items: flex-start;
            & + .section-wrap{
                margin-top: 160px;
            }
            .section-title{
                flex-shrink: 0;
                width: 500px;
                font-weight: 700;
                font-size: 48px;
                line-height: 58px;
                letter-spacing: -0.02em;
                color: #4939B1;
            }
            .section-cont{
                flex: 1 1 0;
                min-width: 0;
                .text-box{
                    min-width: 0;
                    p{
                        font-weight: 400;
                        font-size: 20px;
                        line-height: 32px;
                        color: #000000;
                        & + p{
                            margin-top: 32px;
                        }
                    }
                }
                .cont01-box{
                    padding: 40px 32px;
                    background: #F2F2F2;
                    border-radius: 20px;
                    margin-top: 32px;
                    .box-title{
                        font-weight: 400;
                        font-size: 24px;
                        line-height: 36px;
                        text-align: center;
                        letter-spacing: -0.02em;
                        color: #564F67;
                        span{
                            font-weight: 700;
                            color: #ED8823;
                            &.gr{
                                color: #31A95B;
                            }
                        }
                    }
                    .box-inner{
                        margin-top: 24px;
                        background-color: #fff;
                        padding: 40px 32px;
                        .box-inner-top{
                            .inner-top-list{
                                display: flex;
                                align-items: center;
                                gap: 24px;
                                justify-content: space-between;
                                .inner-list-item{
                                    display: flex;
                                    align-items: center;
                                    gap: 10px;
                                    .item-chip{
                                        padding: 8px 12px;
                                        background-color: #4939B1;
                                        color: #fff;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        text-align: center;
                                        font-weight: 700;
                                        font-size: 18px;
                                        line-height: 28px;
                                        border-radius: 4px;
                                        overflow: hidden;
                                    }
                                    span{
                                        font-weight: 400;
                                        font-size: 18px;
                                        color: #BFB4F7;
                                    }
                                    .item-text{
                                        font-weight: 700;
                                        font-size: 18px;
                                        line-height: 28px;
                                        color: #000000;
                                    }
                                }

                                &.gr{
                                    flex-direction: column;
                                    gap: 8px;
                                    .inner-list-item{
                                        .item-chip{
                                            padding: 16px 24px;
                                        }
                                    }
                                }
                            }
                        }
                        .box-inner-bot{
                            margin-top: 32px;
                            text-align: center;
                            p{
                                font-weight: 700;
                                font-size: 18px;
                                line-height: 28px;
                                color: #38304D;
                            }
                        }
                    }
                    &.box2{
                        padding: 32px;
                        margin-bottom: 32px;
                        .box2-title{
                            font-weight: 600;
                            font-size: 20px;
                            line-height: 30px;
                            letter-spacing: -0.02em;
                            color: #000000;
                        }
                        .box-inner{
                            .box-inner-top{
                                .inner-top-list{
                                    flex-direction: column;
                                    justify-content: start;
                                    align-items: start;
                                    gap: 8px;
                                    .inner-list-item{
                                        .item-chip{
                                            font-weight: 700;
                                            font-size: 18px;
                                        }
                                        .item-text{
                                            font-weight: 400;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                .feature-box{
                    display: grid;
                    margin: 32px 0;
                    height: 200px;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 40px;
                    .box-inner{
                        background: #F2F2F2;
                        border-radius: 999px;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-direction: column;
                        gap: 8px;
                        p{
                            font-weight: 500;
                            font-size: 18px;
                            line-height: 28px;
                            letter-spacing: -0.02em;
                            color: #4939B1;
                        }
                        span{
                            font-weight: 700;
                            font-size: 24px;
                            text-align: center;
                            letter-spacing: -0.02em;
                            color: #000000;
                        }
                    }
                }
                .feature-info-text{
                    font-weight: 700;
                    font-size: 24px;
                    line-height: 38px;
                    text-align: center;
                    color: #4939B1;
                }
            }
        }
    }
    @media (max-width: ${SIZES.TABLET}) {
        padding: 40px 16px 120px;

        overflow: hidden;
        .title-area{
            font-weight: 700;
            font-size: 24px;
            line-height: 36px;
            letter-spacing: -0.02em;
        }
        .visual-wrap {
            height: auto;
            margin: 80px 0;
            .visual-item{
                width: 100%;
                padding: 24px 16px 0;
                .visual-item-desc{
                    padding: 8px 0 24px;
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 18px;
                    text-align: right;
                }
                .visual-item-inner{
                    padding: 18px 8px;
                    & > div {
                        height: 38px;
                        font-weight: 700;
                        font-size: 14px;
                        line-height: 22px;
                    }
                    .half-item{
                        gap: 5px;
                        margin-top: 6px;
                    }
                    .half-item-asymmetry{
                        gap: 5px;
                        margin-top: 6px;
                        &.mt{
                            margin-bottom: 6px;
                        }
                        & > div{
                            &.little{
                                width: 30.5%;
                            }
                        }
                    }
                }

            }
        }
        .section-area{
            .section-wrap{
                align-items: normal;
                flex-direction: column;
                gap: 60px;
                & + .section-wrap{
                    margin-top: 80px;
                }
                .section-title{
                    font-size: 24px;
                    line-height: 36px;
                    letter-spacing: -0.02em;
                }
                .section-cont{
                    flex: 0;
                    .text-box{
                        p{
                            font-weight: 400;
                            font-size: 16px;
                            line-height: 24px;
                        }
                    }
                    .cont01-box{
                        padding: 32px 16px;
                        border-radius: 0;
                        .box-title{
                            font-size: 18px;
                            line-height: 28px;
                        }
                        .box-inner{
                            margin-top: 10px;
                            padding: 40px 8px;
                            .box-inner-top{
                                .inner-top-list{
                                    flex-direction: column;
                                    justify-content: center;
                                    .inner-list-item{
                                        gap: 8px;
                                        flex-direction: column;
                                        .item-chip{
                                            font-size: 16px;
                                            line-height: 24px;
                                            text-align: center;
                                        }
                                        span{
                                            display: none;
                                        }
                                        .item-text{
                                            font-size: 16px;
                                            line-height: 24px;
                                            text-align: center;
                                        }
                                    }

                                    &.gr{
                                        gap: 16px;
                                        .inner-list-item{
                                            .item-chip{
                                              padding: 8px 12px;
                                            }
                                        }
                                    }
                                }
                            }
                            .box-inner-bot{
                                p{
                                    font-size: 16px;
                                    line-height: 24px;
                                    text-align: center;
                                }
                            }
                        }
                        &.box2{
                            margin-bottom: 0;
                            margin-top: 40px;
                            padding: 32px 16px;
                            .box2-title{
                                font-size: 18px;
                                line-height: 28px;
                                text-align: center;
                                letter-spacing: -0.02em;
                            }
                            .box-inner{
                                background-color: transparent;
                                .box-inner-top{
                                    .inner-top-list{
                                        gap: 24px;
                                        .inner-list-item{
                                            align-items: start;
                                            .item-chip{
                                                font-size: 16px;
                                                line-height: 24px;
                                            }
                                            .item-text{
                                                font-size: 16px;
                                                line-height: 24px;
                                                text-align: justify;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    .feature-box{
                        display: block;
                        height: auto;
                        .box-inner{
                            height: 152px;
                            gap: 8px;
                            & + .box-inner{
                                margin-top: 16px;
                            }
                            p{
                                font-weight: 500;
                                font-size: 18px;
                                line-height: 28px;
                                letter-spacing: -0.02em;
                            }
                            span{
                                font-weight: 700;
                                font-size: 24px;
                                line-height: 36px;
                                text-align: center;
                                letter-spacing: -0.02em;
                            }
                        }
                    }
                    .feature-info-text{
                        font-weight: 700;
                        font-size: 18px;
                        line-height: 28px;
                        text-align: center;
                    }
                }
            }
        }
    }
`
export default BusinessField;